import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { t } from 'src/i18n';
import { ROUTES } from 'src/containers/App/routes';
import { isLegalEntityEnabled, isPricingV2Enabled } from '../agencyContractsV2/shared/capabilities';
import actions from './actions';
import { tableConfig } from '../../../settings/index';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import Api, { executeAction } from '../../../helpers/api';

const agenciesApi = new Api('agencies/removed');
const contractsApi = new Api('agency_contracts/removed');

const UUID = /[0-9a-fA-F-]{36}/;

// Under legal entities a row is a contract: `id` stays the contract's for the restore redirect, the agency's
// fields are lifted flat for the shared cells.
const toRow = record => ({
  id: record.id,
  name: record.agency?.name,
  contact: record.agency?.contact,
  actions: record.actions,
});

function* loadData({ payload }) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '' } = payload;
    const byContract = yield select(isLegalEntityEnabled);

    const result = yield call(
      sendRequest,
      byContract ? contractsApi.load : agenciesApi.load,
      { search: { ...payload, page, pageSize, column, order, filter } },
      notifications.REQUEST_ERROR,
    );

    if (!result?.data?.success?.data) {
      logger.error(new Error(`[removedList] ${byContract ? 'agency_contracts' : 'agencies'}/removed answered nothing`));
      yield put(actions.loadDataFailure());

      return;
    }

    const { records, filteredCount, filters, sortableColumns, totalCount } = result.data.success.data;

    yield put(
      actions.loadDataSuccess({
        data: byContract ? (records ?? []).map(toRow) : records,
        filteredTotal: filteredCount,
        filters,
        sortableColumns,
        total: totalCount,
      }),
    );
  } catch (error) {
    logger.error(error);

    yield put(actions.loadDataFailure());
  }
}

// The restore url names the agency, not the contract, so the contract id travels with the row. Under the flag
// the agency page also loads by contract id, and the contract page bounces a legacy body to it itself.
function* restoredPath(id, url) {
  const byContract = yield select(isLegalEntityEnabled);

  if (byContract) {
    if (!id) {
      logger.error(new Error(`[removedList] restored a contract row without an id (${url})`));

      return `/${ROUTES.AGENCIES_ACTIVE_LIST}`;
    }

    const contractPages = yield select(isPricingV2Enabled);

    return contractPages ? `/agencies/contracts/${id}` : `/agencies/active/${id}?tab[]=details`;
  }

  const agencyId = url?.match(UUID)?.[0];

  if (!agencyId) {
    logger.error(new Error(`[removedList] no agency id in the restore url (${url})`));

    return `/${ROUTES.AGENCIES_ACTIVE_LIST}`;
  }

  return `/agencies/active/${agencyId}`;
}

function* restoreAgency({ payload }) {
  try {
    const { id, url, method } = payload;

    const result = yield call(sendRequest, executeAction, { url, method }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.reactivated_successfully', {
            item: t('dash.agencies.viewAgency.agency_label'),
          }),
        }),
      );
      yield put(push(yield* restoredPath(id, url)));

      return;
    }

    logger.error(new Error(`[removedList] restore answered ${JSON.stringify(result)}`));

    // `sendRequest` has already told the operator about a failed request; a refusal comes back as a bare
    // validations map and has not been told.
    if (result !== undefined) {
      yield put(notifications.requestError({ error: t('dash.common.notification.something_went_wrong') }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actions.LOAD_REQUEST, loadData), takeEvery(actions.RESTORE_AGENCY_REQUEST, restoreAgency)]);
}
